import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { canonicalize } from '@/modules/shared/json-canonicalize'
import { requestCenterApi } from '@/services/request-center-api'
import type { WorkflowConfig } from '@kfolks/types'
import { useWorkflowBuilderState } from './WorkflowBuilderState'
import type { WorkflowConfigValidationView } from './useWorkflowConfigValidation'

/**
 * Persistent header bar for the workflow builder. Renders above the
 * tabs so the Save button is reachable from any tab (Visual / History
 * / Raw JSON).
 *
 * **Save action** (per the workflow-builder capability spec): calls
 * the existing admin endpoint `PUT /requests/types/:id` (no new
 * persistence endpoint) with the canonicalized payload —
 * `workflowConfig` is canonicalized via the shared helper so
 * key-order trivia doesn't churn the persisted value (round-trip
 * equality contract from design D5).
 *
 * **Save gate**: the button is disabled when `validation.hasErrors`
 * is true (client-side gate). The backend re-runs the same validators
 * authoritatively; this gate is a UX hint. Warnings (severity
 * `warning`) do NOT block save — only errors do.
 *
 * **Form schema preserved**: the workflow builder authors only
 * `workflowConfig`. The PUT payload deliberately omits `formSchema`
 * and `summaryTemplate` so the backend's partial-update semantics
 * leave them untouched (the form builder owns those on its own route).
 *
 * Per ADR-0012 the canonical shape sent over the wire is
 * `{ version: '1.0', steps: [...] }` — no XState fields.
 */
export function WorkflowBuilderHeader({
	typeId,
	typeName,
	validation,
}: {
	typeId: string | undefined
	typeName?: string | undefined
	validation: WorkflowConfigValidationView
}) {
	const { workflowConfig } = useWorkflowBuilderState()
	const queryClient = useQueryClient()

	const save = useMutation({
		mutationFn: async () => {
			if (!typeId) throw new Error('Missing request type id — cannot save')
			const payload = buildSavePayload(workflowConfig)
			return requestCenterApi.updateRequestType(typeId, payload)
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['request-types'] })
		},
	})

	const blocked = validation.hasErrors
	const blockedReason = blocked
		? `${countErrors(validation.errorsByStepId, validation.topLevelErrors)} validation error(s) — fix to enable save`
		: null

	return (
		<header className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card px-3 py-2">
			<div className="min-w-0">
				<p className="text-xs uppercase tracking-wide text-muted-foreground">Workflow builder</p>
				{typeName ? (
					<p className="truncate text-base font-semibold">{typeName}</p>
				) : typeId ? (
					<p className="truncate font-mono text-xs text-muted-foreground/80">type id: {typeId}</p>
				) : null}
			</div>
			<div className="flex items-center gap-2">
				<SaveStatus
					isPending={save.isPending}
					isSuccess={save.isSuccess}
					error={save.error}
					blockedReason={blockedReason}
				/>
				<Button
					onClick={() => save.mutate()}
					disabled={blocked || save.isPending || !typeId}
					aria-label="Save workflow config"
				>
					<Save className="size-4" />
					{save.isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</header>
	)
}

function buildSavePayload(workflowConfig: WorkflowConfig): { workflowConfig: Record<string, unknown> } {
	// Canonicalize so key-order trivia doesn't churn the persisted value.
	// `Record<string, unknown>` matches the existing `RequestTypeDTO`
	// expectation for `workflowConfig` (the DTO is admin-opaque to the
	// strongly-typed `WorkflowConfig` shape).
	const canonical = canonicalize(workflowConfig) as unknown as Record<string, unknown>
	return { workflowConfig: canonical }
}

function countErrors(byStep: Record<string, string[]>, topLevel: ReadonlyArray<string>): number {
	let n = topLevel.length
	for (const list of Object.values(byStep)) n += list.length
	return n
}

function SaveStatus({
	isPending,
	isSuccess,
	error,
	blockedReason,
}: {
	isPending: boolean
	isSuccess: boolean
	error: unknown
	blockedReason: string | null
}) {
	if (isPending) return null
	if (error) {
		return (
			<span role="alert" className="flex items-center gap-1 text-sm text-destructive">
				<AlertCircle className="size-4" /> {messageFor(error)}
			</span>
		)
	}
	if (blockedReason) {
		return (
			<span className="flex items-center gap-1 text-xs text-destructive" data-testid="save-blocked-reason">
				<AlertCircle className="size-4" /> {blockedReason}
			</span>
		)
	}
	if (isSuccess) {
		return (
			<span className="flex items-center gap-1 text-sm text-emerald-600" data-testid="save-success">
				<Check className="size-4" /> Saved
			</span>
		)
	}
	return null
}

function messageFor(err: unknown): string {
	if (err instanceof Error && err.message) return err.message
	return 'Save failed — try again'
}
